import { PaneLayoutInteractionType } from '@eventbrite/eds-modal';
import { OrderSummary } from '@eventbrite/eds-order-summary';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { formatMajorMoney, formatMinorMoney } from '@eventbrite/intl';
import { Button } from '@eventbrite/marmalade';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import React, { FunctionComponent, ReactNode } from 'react';
import { ConnectedRefundPolicy } from '../../components/refundPolicy/ConnectedRefundPolicy';
import { ORDER_SUMMARY_TITLE } from '../../constants';
import { VirtualIncentive } from '../../models/user';
import RebrandingCloseIcon from '../../resources/rebranding-close-icon';
import { getPaneCollapsibleOffsetForTicketSelection } from '../../utils/getPaneCollapsibleOffsetForTicketSelection';
import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../utils/rebrand';
import { getTotalDiscountingVirtualIncentives } from '../../utils/virtualIncentives';
import defaultStyles from './ItemizedPaneContent.module.scss';
import rebrandStyles from './ItemizedPaneContent.rebranding.module.scss';

const stylesModules = {
    [DEFAULT_STYLES]: defaultStyles,
    [REBRANDING_STYLES]: rebrandStyles,
};

export const ItemizedPaneContent: FunctionComponent<{
    breakdownItems?: Array<{
        content: string | GenericLazyString;
        subcontent?: ReactNode;
        value: string;
        color?: string;
        expansionContent?: Array<{
            content: string;
            value: string;
            id: string;
        }>;
        infoTooltipContent?: string | GenericLazyString;
        valueAdditionalClassNames?: string;
    }>;
    currency: string;
    currencyFormat: string;
    eventDate?: string | null;
    eventImageSrc: string;
    installments?: {
        numberOfInstallments: number;
        totalCostWithInstallments: number;
    };
    isAppRebrandActive: boolean;
    isPaymentView: boolean;
    items: Array<{
        content: string | GenericLazyString;
        subcontent?: ReactNode;
        value: string;
        id: string;
        quantity: string;
        color?: string;
    }>;
    onPaneCloseButtonClick: () => void;
    paneInteraction: typeof PaneLayoutInteractionType;
    selectedVirtualIncentives: Array<VirtualIncentive>;
    totalCost: string;
    totalCostInfo?: string | GenericLazyString | null;
    unformattedOrderCost: number;
}> = ({
    breakdownItems = [],
    currency,
    currencyFormat,
    eventDate,
    eventImageSrc,
    installments,
    isAppRebrandActive,
    isPaymentView,
    items,
    onPaneCloseButtonClick,
    paneInteraction,
    selectedVirtualIncentives,
    totalCost,
    totalCostInfo,
    unformattedOrderCost,
}) => {
    const theme = getStyles(isAppRebrandActive);
    const styles = stylesModules[theme];

    const totalCostConsideringVirtualIncentives = isEmpty(
        selectedVirtualIncentives,
    )
        ? totalCost
        : formatMajorMoney(
              getTotalDiscountingVirtualIncentives(
                  unformattedOrderCost,
                  selectedVirtualIncentives,
              ),
              currency,
              currencyFormat,
          );

    const { isPurchaseFinanced, totalValue } = installments
        ? {
              isPurchaseFinanced: installments.numberOfInstallments > 1,
              totalValue: formatMinorMoney(
                  installments.totalCostWithInstallments,
                  currency,
                  currencyFormat,
              ),
          }
        : {
              isPurchaseFinanced: false,
              totalValue: totalCostConsideringVirtualIncentives,
          };

    const totalContentTitle = isPurchaseFinanced
        ? gettext('Total Purchase Financed')
        : gettext('Total');

    const paneCloseButtonTopClassname = classNames(
        styles.topCloseButtonWrapper,
        { 'eds-l-mar-top-4': isPaymentView },
    );

    const paneCloseButtonTop = isAppRebrandActive ? (
        <div
            className={paneCloseButtonTopClassname}
            data-testid="pane-close-button-top"
        >
            <div onClick={onPaneCloseButtonClick}>
                <RebrandingCloseIcon classname={styles.closeIcon} />
            </div>
        </div>
    ) : null;

    const { paneInteractionClassName, paneCloseButtonBottom } =
        isAppRebrandActive
            ? { paneInteractionClassName: '', paneCloseButtonBottom: null }
            : paneInteraction === PaneLayoutInteractionType.asModal
            ? {
                  paneInteractionClassName: 'eds-l-sn-mar-top-10',
                  paneCloseButtonBottom: (
                      <div
                          className={classNames(
                              'eds-l-pad-top-8 eds-l-pad-bot-16',
                              styles.closeButtonWrapper,
                          )}
                          onClick={onPaneCloseButtonClick}
                      >
                          <Button
                              size="block"
                              data-testid="itemized-pane-content-close-button"
                          >
                              {gettext('Close')}
                          </Button>
                      </div>
                  ),
              }
            : {
                  paneInteractionClassName:
                      'eds-l-sn-mar-top-12 eds-l-mar-bot-10 eds-l-pad-bot-10',
                  paneCloseButtonBottom: null,
              };

    const paneContentClassName = classNames(
        'content',
        'eds-g-cell',
        'eds-g-cell-12-12',
        'eds-g-cell-sw-10-12',
        'eds-g-offset-sw-1-12',
        'eds-l-sn-pad-hor-4',
        'eds-l-sm-pad-hor-6',
        'eds-l-sw-pad-hor-6',
        {
            'eds-l-sm-mar-top-12': !isAppRebrandActive,
            'eds-l-sw-mar-top-12': !isAppRebrandActive,
        },
        paneInteractionClassName,
        {
            'eds-l-mar-top-6': eventImageSrc && !isAppRebrandActive,
            'eds-l-mar-top-20': !eventImageSrc && !isAppRebrandActive,
            'eds-l-mar-top-2': isAppRebrandActive,
            'eds-l-mar-bot-3': isAppRebrandActive,
        },
    );

    const paneHandle =
        isAppRebrandActive &&
        paneInteraction === PaneLayoutInteractionType.asModal ? (
            <div className={styles.paneHandleWrapper} data-testid="pane-handle">
                <div
                    className={styles.paneHandle}
                    onClick={onPaneCloseButtonClick}
                ></div>
            </div>
        ) : null;

    const marginBottom =
        isAppRebrandActive && !isPaymentView
            ? getPaneCollapsibleOffsetForTicketSelection()
            : 0;

    const rootClassName = classNames(styles.root, {
        [styles.rootAsModal]:
            paneInteraction === PaneLayoutInteractionType.asModal,
    });

    return (
        <div
            className={rootClassName}
            style={{ marginBottom: marginBottom }}
            data-testid="order-summary-container"
            id="order-summary-container"
        >
            {paneHandle}
            <div
                className={paneContentClassName}
                data-spec="pane-order-summary"
            >
                {paneCloseButtonTop}
                <OrderSummary
                    breakdownItems={breakdownItems}
                    isAppRebrandActive={isAppRebrandActive}
                    items={items}
                    title={ORDER_SUMMARY_TITLE.toString()}
                    totalContent={totalContentTitle}
                    totalCostInfo={totalCostInfo}
                    totalValue={totalValue}
                    subtitle={eventDate}
                />
                <ConnectedRefundPolicy />
                {paneCloseButtonBottom}
            </div>
        </div>
    );
};
